import {
  ATTENTION_STATUS_BITS,
  type TAttentionStatusActionId,
} from 'partner360/entities/attentionStatuses'

import type {
  ICockpitReminderContext,
  ICockpitReminderPartner,
  ICockpitReminderRequest,
  TCockpitReminderStatus,
  TCockpitReminderType,
} from '../../api/sendCockpitReminder'

/**
 * What a reminder is about: who it reaches (the act) and the status it is raised from. A status of
 * `null` is no status picked, e.g. a person over several statuses; the mail then names every status
 * of every partner.
 */
export interface IAttentionReminderRoute {
  bitValue: number | null
  actionId: TAttentionStatusActionId
}

/** A person to remind, with the partner they are reminded about where the reminder is per partner. */
export interface IAttentionReminderRecipient {
  email: string
  name: string
  partnerId?: string
}

/** Partners of a list reminder: one mail per person naming them all, rather than one per partner. */
export interface IAttentionReminderList {
  partners: ICockpitReminderPartner[]
}

/** The year and offering a reminder is about; the partners come from the recipients or the list. */
export type TAttentionReminderScope = Omit<ICockpitReminderContext, 'partnerId' | 'partners'>

const RECIPIENT_KINDS: Partial<Record<TAttentionStatusActionId, 'pbm' | 'manager'>> = {
  remindPbm: 'pbm',
  remindManager: 'manager',
}

const STATUS_NAMES: Partial<Record<number, TCockpitReminderStatus>> = {
  [ATTENTION_STATUS_BITS.planned]: 'Planned',
  [ATTENTION_STATUS_BITS.finSobQuality]: 'FinSobQuality',
  [ATTENTION_STATUS_BITS.initiativesQuality]: 'InitiativesQuality',
  [ATTENTION_STATUS_BITS.mdf]: 'Mdf',
  [ATTENTION_STATUS_BITS.sobActuals]: 'SobActuals',
  [ATTENTION_STATUS_BITS.h2Review]: 'H2Review',
  [ATTENTION_STATUS_BITS.programRisk]: 'ProgramRisk',
  [ATTENTION_STATUS_BITS.quarterPlan]: 'QuarterPlan',
  [ATTENTION_STATUS_BITS.pendingActions]: 'PendingActions',
}

/** The statuses a manager is reminded about one partner with a mail of its own; the rest go as a summary. */
const MANAGER_PARTNER_STATUSES: TCockpitReminderStatus[] = ['H2Review', 'PendingActions']

/**
 * Reminder type about one partner, per act and status. The service keeps each type's own template
 * and the plan tab its link opens, so the mail names what the status asks for.
 */
export const getAttentionReminderType = ({
  actionId,
  bitValue,
}: IAttentionReminderRoute): TCockpitReminderType | undefined => {
  const kind = RECIPIENT_KINDS[actionId]
  const status = bitValue === null ? undefined : STATUS_NAMES[bitValue]

  if (!kind || !status || (kind === 'manager' && !MANAGER_PARTNER_STATUSES.includes(status))) {
    return undefined
  }

  return `${kind}${status}` as TCockpitReminderType
}

/** Reminder type about a list of partners: one status, or every status when none is picked. */
export const getAttentionReminderListType = ({
  actionId,
  bitValue,
}: IAttentionReminderRoute): TCockpitReminderType | undefined => {
  const kind = RECIPIENT_KINDS[actionId]
  const status = bitValue === null ? undefined : STATUS_NAMES[bitValue]

  if (!kind) {
    return undefined
  }

  return status ? `${kind}${status}Partners` : `${kind}Summary`
}

const groupByPartner = (recipients: IAttentionReminderRecipient[]) =>
  recipients.reduce<Map<string, IAttentionReminderRecipient[]>>((groups, recipient) => {
    if (recipient.partnerId) {
      groups.set(recipient.partnerId, [...(groups.get(recipient.partnerId) ?? []), recipient])
    }

    return groups
  }, new Map())

const toAddressee = ({ email, name }: IAttentionReminderRecipient) => ({ email, name })

/**
 * The `reminder/send` (and `reminder/check`) requests a reminder makes. The one place that decides
 * which reminder type goes out, so a button only says who, about what and about which partners.
 *
 * - With a `list`, one request names all its partners (`<recipient><Status>Partners`, or
 *   `<recipient>Summary` without a status).
 * - Without one, one request per partner the recipients carry: the status' own type; where the
 *   recipient has none about one partner, the list type of the status with that one partner; and
 *   for a status with no type at all, the summary naming that one status.
 *
 * Nothing goes out without a year, a partner or a type: the list is empty then.
 */
export const buildAttentionReminderRequests = (
  recipients: IAttentionReminderRecipient[],
  route: IAttentionReminderRoute,
  scope: TAttentionReminderScope | undefined,
  list?: IAttentionReminderList
): ICockpitReminderRequest[] => {
  if (!scope || recipients.length === 0) {
    return []
  }

  const listType = getAttentionReminderListType(route)

  if (list) {
    return listType && list.partners.length > 0
      ? [{ type: listType, to: recipients.map(toAddressee), context: { ...scope, ...list } }]
      : []
  }

  const type = getAttentionReminderType(route)
  const { bitValue } = route

  return [...groupByPartner(recipients)].flatMap(([partnerId, to]): ICockpitReminderRequest[] => {
    const addressees = to.map(toAddressee)

    if (type) {
      return [{ type, to: addressees, context: { ...scope, partnerId } }]
    }

    if (!listType || bitValue === null) {
      return []
    }

    return [
      {
        type: listType,
        to: addressees,
        context: { ...scope, partners: [{ partnerId, statuses: [bitValue] }] },
      },
    ]
  })
}
